import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { useNavigate } from 'react-router-dom';
import { getSocket } from '../context/socket';
import { Avatar } from 'primereact/avatar';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import ScheduleConflictModal from './ScheduleConflictModal';

function isPriorityTask(details) {
    if (!details) return false;
    if (['Urgent', 'High'].includes(details.priority)) return true;
    const ep = details.project?.execution_priority || details.execution_priority;
    return ep === 'P1' || ep === 'P2';
}

const ADMIN_GANTT_ROLES = ['SuperAdmin', 'Admin', 'Founder', 'Task_manager', 'Department_Head'];

function memberLabel(user) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User #${user.user_id}`;
}

const CardMemberPage = ({ onCancel, cardDetails, togglePopup, cardId, boardId }) => {
    const socket = getSocket();
    const navigate = useNavigate();
    const { userId, accessToken, role } = useAuth();
    const pendingPriorityNav = useRef(null);
    const onCancelRef = useRef(onCancel);
    const navigateRef = useRef(navigate);
    const cardIdRef = useRef(cardId);

    useEffect(() => {
        onCancelRef.current = onCancel;
        navigateRef.current = navigate;
        cardIdRef.current = cardId;
    }, [onCancel, navigate, cardId]);

    const performPriorityRedirect = useCallback(() => {
        const pending = pendingPriorityNav.current;
        if (!pending) return false;
        pendingPriorityNav.current = null;
        onCancelRef.current?.();
        navigateRef.current(
            `/Graphe/dashboard/admin_user_gantt?user_id=${pending.assigneeId}&trigger_card_id=${cardIdRef.current}`
        );
        return true;
    }, []);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState('');
    const [pickerValue, setPickerValue] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [allocations, setAllocations] = useState({});
    const [saveError, setSaveError] = useState('');
    const [scheduleWarnings, setScheduleWarnings] = useState([]);
    const [priorityWarnings, setPriorityWarnings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [successDialog, setSuccessDialog] = useState(null);
    const [approvePreview, setApprovePreview] = useState(null);

    const dismissSuccessDialog = () => {
        const shouldClose = successDialog?.closeMembersOnDismiss;
        setSuccessDialog(null);
        if (shouldClose) {
            onCancel();
        }
    };

    const estimatedHours =
        cardDetails?.estimated_hours != null ? Number(cardDetails.estimated_hours) : null;

    const showHourInputs = estimatedHours != null && selectedUsers.length > 0;

    const resolveAllocationsForSave = useCallback(() => {
        const resolved = { ...allocations };
        if (estimatedHours != null && selectedUsers.length === 1) {
            const uid = selectedUsers[0].user_id;
            if (resolved[uid] == null || Number(resolved[uid]) <= 0) {
                resolved[uid] = estimatedHours;
            }
        }
        return resolved;
    }, [allocations, estimatedHours, selectedUsers]);

    const allocationSum = useMemo(() => {
        const resolved = resolveAllocationsForSave();
        return selectedUsers.reduce(
            (sum, user) => sum + (Number(resolved[user.user_id]) || 0),
            0
        );
    }, [selectedUsers, resolveAllocationsForSave]);

    const availableUsers = useMemo(
        () =>
            users.filter(
                (user) => !selectedUsers.some((u) => Number(u.user_id) === Number(user.user_id))
            ),
        [users, selectedUsers]
    );

    const sortedAvailableUsers = useMemo(
        () =>
            [...availableUsers].sort((a, b) =>
                memberLabel(a).localeCompare(memberLabel(b), undefined, { sensitivity: 'base' })
            ),
        [availableUsers]
    );

    useEffect(() => {
        if (!boardId || !accessToken) {
            setUsers([]);
            setUsersLoading(false);
            return;
        }

        let cancelled = false;
        const loadMembers = async () => {
            setUsersLoading(true);
            setUsersError('');
            try {
                const res = await axios.get(
                    `${config.apiBASEURL}/TaskBoardRoutes/boards/${boardId}/eligible-members`,
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                if (!cancelled) {
                    setUsers(res.data?.users || []);
                }
            } catch (err) {
                console.error('Failed to load board members', err);
                if (!cancelled) {
                    setUsers([]);
                    setUsersError(
                        err.response?.data?.message || 'Could not load board members.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setUsersLoading(false);
                }
            }
        };

        loadMembers();
        return () => {
            cancelled = true;
        };
    }, [boardId, accessToken]);

    useEffect(() => {
        if (!socket) return;

        const onMembersError = (err) => {
            setSaveError(err?.error || 'Failed to save members');
            pendingPriorityNav.current = null;
            setSaving(false);
        };
        const onMembersUpdated = (payload) => {
            if (Number(payload?.card_id) !== Number(cardIdRef.current)) return;
            performPriorityRedirect();
        };

        socket.on('errorUpdatingCardMembers', onMembersError);
        socket.on('cardMembersUpdated', onMembersUpdated);

        return () => {
            socket.off('errorUpdatingCardMembers', onMembersError);
            socket.off('cardMembersUpdated', onMembersUpdated);
        };
    }, [socket, performPriorityRedirect]);

    useEffect(() => {
        if (!cardDetails?.members?.length) {
            setSelectedUsers([]);
            setAllocations({});
            return;
        }

        setSelectedUsers(cardDetails.members);
        const initial = {};
        (cardDetails.member_allocations || cardDetails.members).forEach((m) => {
            const uid = m.user_id;
            const hours =
                m.allocated_hours != null
                    ? Number(m.allocated_hours)
                    : cardDetails.member_allocations?.find((a) => a.user_id === uid)
                          ?.allocated_hours;
            if (hours != null) initial[uid] = Number(hours);
        });

        if (
            estimatedHours != null &&
            cardDetails.members.length === 1 &&
            initial[cardDetails.members[0].user_id] == null
        ) {
            initial[cardDetails.members[0].user_id] = estimatedHours;
        }

        setAllocations(initial);
    }, [cardDetails, estimatedHours]);

    const handleUserSelect = (user) => {
        if (!user?.user_id) return;

        if (cardDetails?.task_type === 'Post') {
            setSelectedUsers([user]);
            setAllocations(
                estimatedHours != null ? { [user.user_id]: estimatedHours } : {}
            );
        } else if (!selectedUsers.find((u) => Number(u.user_id) === Number(user.user_id))) {
            setSelectedUsers((prev) => {
                const next = [...prev, user];
                if (estimatedHours != null && next.length === 1) {
                    setAllocations({ [user.user_id]: estimatedHours });
                }
                return next;
            });
        }
    };

    const handleDropdownChange = (e) => {
        const id = e.target.value;
        setPickerValue(id);
        if (!id) return;
        const user = sortedAvailableUsers.find((u) => String(u.user_id) === id);
        if (user) handleUserSelect(user);
        setPickerValue('');
    };

    const removeUser = (userIdToRemove) => {
        if (cardDetails?.task_type === 'Post') return;

        setSelectedUsers((prev) => {
            const next = prev.filter((u) => Number(u.user_id) !== Number(userIdToRemove));
            if (estimatedHours != null && next.length === 1) {
                setAllocations({ [next[0].user_id]: estimatedHours });
            } else {
                setAllocations((current) => {
                    const updated = { ...current };
                    delete updated[userIdToRemove];
                    return updated;
                });
            }
            return next;
        });
    };

    const updateAllocation = (memberUserId, hours) => {
        const parsed = hours == null || hours === '' ? null : Number(hours);
        setAllocations((prev) => ({ ...prev, [memberUserId]: parsed }));
    };

    const buildMemberSavePayload = useCallback(() => {
        const user_ids = selectedUsers.map((u) => u.user_id);
        const resolvedAllocations = resolveAllocationsForSave();
        const allocationPayload = selectedUsers.map((u) => ({
            user_id: u.user_id,
            allocated_hours: resolvedAllocations[u.user_id] ?? null,
        }));
        return { user_ids, resolvedAllocations, allocationPayload };
    }, [selectedUsers, resolveAllocationsForSave]);

    const fetchScheduleConflicts = useCallback(
        async (user_ids, resolvedAllocations) => {
            if (!accessToken || !user_ids.length) {
                return { warnings: [], priority: [] };
            }
            const warnings = [];
            const priority = [];
            for (const uid of user_ids) {
                const res = await axios.post(
                    `${config.apiBASEURL}/TaskBoardRoutes/schedule/check-conflicts`,
                    {
                        user_id: uid,
                        card_id: cardId,
                        proposed_allocated_hours: resolvedAllocations[uid] ?? null,
                    },
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                (res.data?.conflicts || []).forEach((c) => {
                    warnings.push({ ...c, assignee_id: uid });
                });
                (res.data?.priority_warnings || []).forEach((w) => {
                    priority.push({ ...w, assignee_id: uid });
                });
            }
            return { warnings, priority };
        },
        [accessToken, cardId]
    );

    const validateAllocationSum = useCallback(() => {
        if (
            estimatedHours != null &&
            selectedUsers.length > 1 &&
            Math.abs(allocationSum - estimatedHours) > 0.01
        ) {
            setSaveError(
                `Allocated hours (${allocationSum}h) must equal estimated hours (${estimatedHours}h).`
            );
            return false;
        }
        return true;
    }, [estimatedHours, selectedUsers.length, allocationSum]);

    const preparePriorityNavIfNeeded = useCallback(() => {
        const previousIds = new Set(
            (cardDetails?.members || []).map((m) => Number(m.user_id))
        );
        const newAssignees = selectedUsers.filter(
            (u) => !previousIds.has(Number(u.user_id))
        );
        if (
            isPriorityTask(cardDetails) &&
            newAssignees.length &&
            ADMIN_GANTT_ROLES.includes(role)
        ) {
            pendingPriorityNav.current = { assigneeId: newAssignees[0].user_id };
        } else {
            pendingPriorityNav.current = null;
        }
    }, [cardDetails, selectedUsers, role]);

    const persistMembers = useCallback(
        (user_ids, allocationPayload) =>
            new Promise((resolve, reject) => {
                if (!socket) {
                    reject(new Error('Connection lost. Please refresh the page and try again.'));
                    return;
                }
                setSaving(true);
                socket.emit(
                    'addOrUpdateCardMembers',
                    {
                        card_id: cardId,
                        user_ids,
                        board_id: boardId,
                        allocations: allocationPayload,
                        user_id: userId,
                    },
                    (response) => {
                        setSaving(false);
                        if (!response?.success) {
                            reject(new Error(response?.error || 'Failed to save members'));
                            return;
                        }
                        if (Number(response.card_id) !== Number(cardId)) {
                            reject(new Error('Save response did not match this card'));
                            return;
                        }
                        resolve(response);
                    }
                );
            }),
        [socket, cardId, boardId, userId]
    );

    const completeMemberSaveSuccess = useCallback(
        (dialog) => {
            setScheduleWarnings([]);
            setPriorityWarnings([]);
            setSaveError('');
            if (performPriorityRedirect()) {
                return true;
            }
            if (dialog) {
                setSuccessDialog(dialog);
            }
            return false;
        },
        [performPriorityRedirect]
    );

    const handleSaveMembers = async (force = false) => {
        setSaveError('');
        if (!force) {
            setScheduleWarnings([]);
            setPriorityWarnings([]);
        }

        if (!validateAllocationSum()) return;

        const { user_ids, resolvedAllocations, allocationPayload } = buildMemberSavePayload();
        if (!user_ids.length) {
            setSaveError('Select at least one member.');
            return;
        }

        if (!force && accessToken) {
            try {
                const { warnings, priority } = await fetchScheduleConflicts(
                    user_ids,
                    resolvedAllocations
                );
                if (warnings.length || priority.length) {
                    setScheduleWarnings(warnings);
                    setPriorityWarnings(priority);
                    return;
                }
            } catch (err) {
                console.warn('Schedule conflict check failed', err);
            }
        }

        preparePriorityNavIfNeeded();
        try {
            await persistMembers(user_ids, allocationPayload);
            completeMemberSaveSuccess({
                title: 'Members saved',
                message: 'Card members were updated successfully.',
                closeMembersOnDismiss: true,
            });
        } catch (err) {
            setSaveError(err.message);
        }
    };

    const handleRequestApproval = async (items) => {
        if (!accessToken || actionLoading) return;
        const user_ids = selectedUsers.map((u) => u.user_id);
        if (!user_ids.length) {
            setSaveError('Select at least one member before requesting approval.');
            return;
        }
        const resolvedAllocations = resolveAllocationsForSave();
        const allocationPayload = selectedUsers.map((u) => ({
            user_id: u.user_id,
            allocated_hours: resolvedAllocations[u.user_id] ?? null,
        }));
        setActionLoading(true);
        setSaveError('');
        try {
            await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/approvals`,
                {
                    request_type: 'assignment_conflict',
                    user_id: user_ids[0],
                    card_id: cardId,
                    reason: items.map((i) => i.message).join('; '),
                    payload: {
                        conflicts: items,
                        board_id: boardId,
                        user_ids,
                        allocations: allocationPayload,
                    },
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setScheduleWarnings([]);
            setPriorityWarnings([]);
            setSuccessDialog({
                title: 'Admin approval sent',
                message:
                    'Your assignment request was sent to an admin for review. You will be notified once it is decided.',
                closeMembersOnDismiss: true,
            });
        } catch (err) {
            setSaveError(err.response?.data?.message || 'Failed to submit approval request');
        } finally {
            setActionLoading(false);
        }
    };

    const buildApproveContext = (targetCardId, conflictItems = []) => {
        const scheduleConflicts = conflictItems.filter((c) => c.source !== 'priority');
        const insufficientConflict = scheduleConflicts.find(
            (c) => c.type === 'insufficient_hours'
        );
        const deficitHours = Number(insufficientConflict?.deficit_hours) || 0;

        const spillCardIds = [
            ...new Set(
                scheduleConflicts
                    .filter((c) => c.card_id && Number(c.spill_hours) > 0)
                    .map((c) => Number(c.card_id))
            ),
        ];
        if (!spillCardIds.length && targetCardId) {
            spillCardIds.push(Number(targetCardId));
        }

        const assigneeId =
            scheduleConflicts.find((c) => c.assignee_id)?.assignee_id ||
            scheduleConflicts.find((c) => c.user_id)?.user_id ||
            selectedUsers[0]?.user_id;

        const resolvedAllocations = resolveAllocationsForSave();
        const proposedAllocatedHours =
            assigneeId != null ? resolvedAllocations[assigneeId] ?? null : null;

        return {
            scheduleConflicts,
            insufficientConflict,
            deficitHours,
            spillCardIds,
            assigneeId,
            proposedAllocatedHours,
            resolveInsufficientHours: Boolean(insufficientConflict),
        };
    };

    const handleApproveTimeline = async (targetCardId, conflictItems = []) => {
        if (!accessToken || actionLoading) return;

        const context = buildApproveContext(targetCardId, conflictItems);
        const {
            spillCardIds,
            assigneeId,
            proposedAllocatedHours,
            deficitHours,
            resolveInsufficientHours,
        } = context;

        if (!validateAllocationSum()) return;

        const { user_ids, allocationPayload } = buildMemberSavePayload();
        if (!user_ids.length) {
            setSaveError('Select at least one member before approving.');
            return;
        }

        setActionLoading(true);
        setSaveError('');
        try {
            const res = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/resolve-conflict`,
                {
                    action: 'preview_approve_timeline',
                    user_id: userId,
                    payload: {
                        card_ids: spillCardIds,
                        assignee_id: assigneeId,
                        proposed_allocated_hours: proposedAllocatedHours,
                        editing_card_id: cardId,
                        resolve_insufficient_hours: resolveInsufficientHours,
                        deficit_hours: deficitHours,
                    },
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            const result = res.data?.result || {};
            setApprovePreview({
                ...context,
                changes: result.changes || [],
                conflictMode: result.conflict_mode || null,
                user_ids,
                allocationPayload,
                members: selectedUsers.map((u) => ({
                    user_id: u.user_id,
                    name: memberLabel(u),
                    allocated_hours:
                        allocationPayload.find((row) => row.user_id === u.user_id)
                            ?.allocated_hours ?? null,
                })),
            });
        } catch (err) {
            setSaveError(
                err.response?.data?.message || err.message || 'Could not load change preview'
            );
        } finally {
            setActionLoading(false);
        }
    };

    const executeConfirmApproveAndSave = async () => {
        if (!approvePreview || actionLoading) return;

        const {
            spillCardIds,
            assigneeId,
            proposedAllocatedHours,
            deficitHours,
            resolveInsufficientHours,
            user_ids,
            allocationPayload,
        } = approvePreview;

        setActionLoading(true);
        setSaveError('');
        try {
            const res = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/resolve-conflict`,
                {
                    action: 'approve_timeline_batch',
                    user_id: userId,
                    payload: {
                        card_ids: spillCardIds,
                        assignee_id: assigneeId,
                        proposed_allocated_hours: proposedAllocatedHours,
                        editing_card_id: cardId,
                        resolve_insufficient_hours: resolveInsufficientHours,
                        deficit_hours: deficitHours,
                    },
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            const appliedChanges = res.data?.result?.changes || approvePreview.changes || [];
            for (const change of appliedChanges) {
                if (socket && change.board_id && change.new_due_date) {
                    socket.emit(
                        'updateCardDates',
                        {
                            card_id: change.card_id,
                            board_id: change.board_id,
                            due_date: moment(change.new_due_date)
                                .tz('Asia/Kolkata')
                                .format('YYYY-MM-DD HH:mm:ss'),
                            user_id: userId,
                        },
                        () => {}
                    );
                }
            }

            preparePriorityNavIfNeeded();
            await persistMembers(user_ids, allocationPayload);

            setApprovePreview(null);
            setScheduleWarnings([]);
            setPriorityWarnings([]);
            setSaveError('');

            if (performPriorityRedirect()) {
                return;
            }

            onCancel();
        } catch (err) {
            setSaveError(
                err.response?.data?.message || err.message || 'Could not approve and save'
            );
        } finally {
            setActionLoading(false);
        }
    };

    const canAddMore =
        !usersLoading &&
        !usersError &&
        availableUsers.length > 0 &&
        !(cardDetails?.task_type === 'Post' && selectedUsers.length >= 1);

    return (
        <div className="popup-box popup-members card-member-popup">
            <div className="popup-header">
                Members
                <Button icon="pi pi-times" className="p-button-text close-btn" onClick={onCancel} />
            </div>
            <div className="popup-body">
                {showHourInputs && selectedUsers.length > 1 && (
                    <p className="small text-muted mb-2">
                        Split <strong>{estimatedHours}h</strong> across members (sum must match).
                        Current sum: <strong>{allocationSum}h</strong>
                    </p>
                )}
                {showHourInputs && selectedUsers.length === 1 && (
                    <p className="small text-muted mb-2">
                        Estimated <strong>{estimatedHours}h</strong> for this card — assign to the
                        member below.
                    </p>
                )}

                {canAddMore && (
                    <>
                        <label className="small text-muted d-block mb-1">
                            {cardDetails?.task_type === 'Post' ? 'Assign to' : 'Add member'}
                        </label>
                        <select
                            className="form-select card-member-select"
                            value={pickerValue}
                            onChange={handleDropdownChange}
                        >
                            <option value="">
                                {cardDetails?.task_type === 'Post'
                                    ? 'Select assignee…'
                                    : 'Select a member…'}
                            </option>
                            {sortedAvailableUsers.map((user) => (
                                <option key={user.user_id} value={user.user_id}>
                                    {memberLabel(user)}
                                </option>
                            ))}
                        </select>
                        <p className="small text-muted mt-1 mb-0">
                            Only members linked to this board are listed.
                        </p>
                    </>
                )}

                {usersLoading && (
                    <p className="small text-muted mb-0">Loading board members…</p>
                )}
                {usersError && <p className="text-danger small mb-0">{usersError}</p>}
                {!usersLoading && !usersError && users.length === 0 && (
                    <p className="text-warning small mb-0">
                        No assignable users on this board. Add members to the board first.
                    </p>
                )}
                {!usersLoading &&
                    !usersError &&
                    users.length > 0 &&
                    availableUsers.length === 0 &&
                    selectedUsers.length > 0 && (
                        <p className="small text-muted mb-0 mt-2">
                            All board members are selected. Remove someone to add another.
                        </p>
                    )}

                <div className="selected-members mt-3">
                    <p className="mb-2">
                        <b>Selected Members</b>
                        {selectedUsers.length > 0 && (
                            <span className="text-muted small"> ({selectedUsers.length})</span>
                        )}
                    </p>
                    {selectedUsers.length === 0 && (
                        <p className="small text-muted mb-0">No members selected yet.</p>
                    )}
                    <ul className="list-EmpLIst card-member-list">
                        {selectedUsers.map((user) => (
                            <li key={user.user_id}>
                                <div className="card-member-row-main">
                                    <Avatar
                                        image={
                                            user.profileImage?.image_url ||
                                            'https://primefaces.org/cdn/primereact/images/avatar/amyelsner.png'
                                        }
                                        shape="circle"
                                        size="normal"
                                    />
                                    <span className="card-member-name">{memberLabel(user)}</span>
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-text text-danger p-0 flex-shrink-0"
                                        onClick={() => removeUser(user.user_id)}
                                        disabled={cardDetails?.task_type === 'Post'}
                                        aria-label={`Remove ${memberLabel(user)}`}
                                    />
                                </div>
                                {showHourInputs && (
                                    <div className="card-member-hours-row">
                                        <InputNumber
                                            value={
                                                allocations[user.user_id] ??
                                                (selectedUsers.length === 1
                                                    ? estimatedHours
                                                    : null)
                                            }
                                            onValueChange={(e) =>
                                                updateAllocation(user.user_id, e.value)
                                            }
                                            min={0}
                                            max={999}
                                            minFractionDigits={0}
                                            maxFractionDigits={2}
                                            useGrouping={false}
                                            suffix=" h"
                                            placeholder="Hours"
                                            className="w-100"
                                        />
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>

                {saveError && <p className="text-danger small mt-2 mb-0">{saveError}</p>}

                <ScheduleConflictModal
                    conflicts={scheduleWarnings}
                    priorityWarnings={priorityWarnings}
                    editingCardId={cardId}
                    onProceed={() => handleSaveMembers(true)}
                    onRequestApproval={handleRequestApproval}
                    onApproveTimeline={handleApproveTimeline}
                    actionLoading={saving || actionLoading}
                />

                <Button
                    severity="success"
                    label={saving ? 'Saving…' : 'Save'}
                    className="border-0 py-1 px-3 mt-3"
                    disabled={saving || actionLoading}
                    onClick={() => handleSaveMembers()}
                />
            </div>

            <Dialog
                visible={!!approvePreview}
                onHide={() => !actionLoading && setApprovePreview(null)}
                header="Review changes before saving"
                modal
                className="card-member-preview-dialog"
                style={{ width: 'min(480px, 94vw)' }}
                closable={!actionLoading}
                draggable={false}
            >
                <p className="small text-muted mb-3">
                    {approvePreview?.conflictMode === 'insufficient_hours'
                        ? 'This assignee needs more working hours than fit before current deadlines. Confirming will extend due date(s) to create schedule room, then save members.'
                        : 'Tasks where work runs past the due date will be extended. Due dates are never shortened.'}
                </p>

                {approvePreview?.insufficientConflict && (
                    <p className="small mb-3">
                        Capacity gap: needs{' '}
                        <strong>{approvePreview.insufficientConflict.required_hours}h</strong> but
                        only{' '}
                        <strong>{approvePreview.insufficientConflict.available_hours}h</strong>{' '}
                        fit (
                        <strong>{approvePreview.deficitHours}h</strong> short).
                    </p>
                )}

                {approvePreview?.changes?.length > 0 && (
                    <div className="mb-3">
                        <strong className="small d-block mb-2">Timeline adjustments</strong>
                        <ul className="schedule-preview-list mb-0">
                            {approvePreview.changes.map((change) => (
                                <li key={change.card_id} className="schedule-preview-item">
                                    <div className="schedule-preview-title">
                                        {change.card_title}{' '}
                                        <span className="text-muted">#{change.card_id}</span>
                                    </div>
                                    <div className="schedule-preview-row">
                                        <span className="text-muted">Work window</span>
                                        <span>
                                            {change.scheduled_start_at_ist || '—'} →{' '}
                                            {change.scheduled_end_at_ist || '—'}
                                        </span>
                                    </div>
                                    <div className="schedule-preview-row">
                                        <span className="text-muted">Due date</span>
                                        <span>
                                            {change.previous_due_date_ist || 'Not set'} →{' '}
                                            <strong>{change.new_due_date_ist || '—'}</strong>
                                        </span>
                                    </div>
                                    {change.capacity_note && (
                                        <div className="schedule-preview-note text-muted">
                                            {change.capacity_note}
                                        </div>
                                    )}
                                    {change.spill_hours > 0 && (
                                        <div className="schedule-preview-note text-muted">
                                            Clears ~{change.spill_hours}h spill past due
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {approvePreview?.members?.length > 0 && (
                    <div className="mb-3">
                        <strong className="small d-block mb-2">Members to save</strong>
                        <ul className="schedule-preview-list mb-0">
                            {approvePreview.members.map((member) => (
                                <li key={member.user_id} className="schedule-preview-item">
                                    {member.name}
                                    {member.allocated_hours != null && (
                                        <span className="text-muted">
                                            {' '}
                                            · {member.allocated_hours}h
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="card-member-success-actions">
                    <Button
                        label="Cancel"
                        severity="secondary"
                        className="border-0 py-1 px-3 me-2"
                        disabled={actionLoading}
                        onClick={() => setApprovePreview(null)}
                    />
                    <Button
                        label={actionLoading ? 'Saving…' : 'Confirm & save'}
                        className="border-0 py-1 px-3"
                        disabled={actionLoading}
                        onClick={executeConfirmApproveAndSave}
                    />
                </div>
            </Dialog>

            <Dialog
                visible={!!successDialog}
                onHide={dismissSuccessDialog}
                header={successDialog?.title}
                modal
                className="card-member-success-dialog"
                style={{ width: 'min(420px, 92vw)' }}
                closable={false}
                draggable={false}
            >
                <p className="card-member-success-message">{successDialog?.message}</p>
                <div className="card-member-success-actions">
                    {successDialog?.actionLink && (
                        <Button
                            label={successDialog.actionLabel || 'View timeline'}
                            severity="info"
                            className="border-0 py-1 px-3 me-2"
                            onClick={() => {
                                navigate(successDialog.actionLink);
                                dismissSuccessDialog();
                            }}
                        />
                    )}
                    <Button
                        label={successDialog?.dismissLabel || 'Done'}
                        className="border-0 py-1 px-3"
                        onClick={dismissSuccessDialog}
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default CardMemberPage;
